/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { tableHeaderGray, tableStripedRowDark, tableStripedRowLight } from '../../../../constants/colors';
import { selectUserState } from '../../../../redux/ducks/User/slice';
import { useAppSelector } from '../../../../redux/hooks';
import ArgosFlexbox from '../../../components/Containers/ArgosFlexbox';
import { StyledArgosDialog } from '../../../components/Dialogs/ArgosDialog';
import ObjectsColumnPopup from '../../../components/Dialogs/RedlineAppDialogs/ObjectsColumnPopup';
import DashboardStudySocket from '../ItemDetails/Tabs/ExecutionStatusTab/DashboardStudySocket';
import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";

interface Props {
  isLoadingInitialData: boolean;
  columns: any;
  data: any;
  isLandingPage: boolean;
  defaultColumnGrouping?: any; // Group datasets by Type, Name...etc
  isFetching: boolean;
  studyId: string;
  studyIds: string[];
  onlyShowInProgressDatasets?: boolean;
}

/**
 * Renders a table of datasets for Coverage App Requirements used in the Landing Page, 
 * Dashboard and Execution Status Tabs.
 *
 * @component
 * @param {Props} props - The component props.
 * @returns {JSX.Element} The rendered table component.
 */
const CovAppReqTable: React.FC<Props> = (props) => {
  const [columnVisibility, setColumnVisibility] = useState<any>({
    creator: false,
    owner: false,
    studyId: false
  })
  const defaultColumnGrouping = useMemo(() => props.studyId ? ['type'] : ['studyId'], [props.studyId])
  const isObjectsPopupVisible = useAppSelector(state => selectUserState(state).isObjectsPopupVisible)
  const [currentRowData, setCurrentRowData] = useState<any>()
  const landingPageColumnsToHide = useMemo(() => {
    let columnsToHide = ['dateGenerated', 'endTime', 'runTime', 'generator']
    if (props.onlyShowInProgressDatasets) {
      columnsToHide.push('status')
    }
    return columnsToHide
  }, [])

  const dashboardSockets = props.isLandingPage ? 
    <>{props.studyIds?.map((value, index) => <DashboardStudySocket studyId={value} />)}</> : 
    <DashboardStudySocket studyId={props.studyId ?? ''} />
  
  useEffect(() => {
    if (props.isLandingPage) {
      setColumnVisibility((_prevState: any) => {
        return (
          {
            ...columnVisibility,
            ...landingPageColumnsToHide.reduce((acc: any, key) => {
              acc[key] = false
              return acc
            }, {}),
            studyId: true,
          }
        )
      })
    }
  }, [props.isLandingPage]);

  
  return (
    <ArgosFlexbox vertical style={{ overflow: 'auto', height: '100%' }}>
      {dashboardSockets}

      <ArgosTableSimple
        columns={props.columns ?? []}
        data={props.data ?? []}
        enablePagination={false}
        enableRowVirtualization={true}
        initialState={{
          density: 'compact',
          grouping: props.defaultColumnGrouping ?? defaultColumnGrouping,
          expanded: props.isLandingPage ? true : {},
        }}
        onColumnVisibilityChange={(columnVisibility) => {
          setColumnVisibility(columnVisibility)
        }}
        state={{ isLoading: props.isFetching || props.isLoadingInitialData, columnVisibility }}
        enableColumnDragging={false}
        enableStickyFooter
        enableGrouping={true}
        enableExpanding={true}
        muiExpandButtonProps={({ row }) => ({
          // Show expand button only when row has sub-elements
          sx: { display: row?.subRows?.length ? 'flex' : 'none' }
        })}
        muiTableBodyProps={{
          // Table row style - striping
          sx: {
            '& tr:nth-of-type(odd)': { backgroundColor: tableStripedRowDark },
            '& tr:nth-of-type(even)': { backgroundColor: tableStripedRowLight },
          },
        }}
        muiTableHeadRowProps={{
          // Header row style
          sx: { backgroundColor: tableHeaderGray },
        }}
      />

      <StyledArgosDialog
        open={isObjectsPopupVisible}
        maxWidth={'30w'}
        minWidth={'30vw'}
        height={'25vw'}
      >
        <ObjectsColumnPopup row={currentRowData} />
      </StyledArgosDialog>
    </ArgosFlexbox>
  );
};

export default CovAppReqTable;